import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Navbar } from '../../components/Layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import AdminComplaintsPanel from './AdminComplaintsPanel';
import { 
  Clock, CheckCircle, XCircle, Users, Shield, RefreshCw, Loader2, 
  Eye, FileText, CheckSquare, Square, ExternalLink, Search, Building2,
  MapPin, Mail, ChevronLeft, ChevronRight, Flag, Award, AlertTriangle, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('verifications');
  const [stats, setStats] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('verified');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  
  // Server Directory state
  const [servers, setServers] = useState([]);
  const [serverFilters, setServerFilters] = useState({ state: '', subscription_status: '', search: '' });
  const [serverPage, setServerPage] = useState(1);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPages, setServerPages] = useState(0);
  const [serversLoading, setServersLoading] = useState(false);
  
  // Server Credentials modal state
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentialsData, setCredentialsData] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  
  // Document preview state
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Complaints stats only
  const [complaintStats, setComplaintStats] = useState(null);

  useEffect(function() {
    loadData();
  }, []);

  useEffect(function() {
    if (activeTab === 'servers') {
      loadServers();
    }
  }, [activeTab, serverFilters, serverPage]);

  const loadData = async function() {
    try {
      const [statsRes, docsRes, complaintStatsRes] = await Promise.all([
        api.getAdminStats(),
        api.getPendingDocuments(),
        api.getComplaintStats()
      ]);
      setStats(statsRes.data);
      // API returns array directly, not { documents: [...] }
      setPendingDocs(Array.isArray(docsRes.data) ? docsRes.data : docsRes.data.documents || []);
      setComplaintStats(complaintStatsRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadServers = async function() {
    setServersLoading(true);
    try {
      const params = {
        page: serverPage,
        limit: 15,
        ...(serverFilters.state && { state: serverFilters.state }),
        ...(serverFilters.subscription_status && { subscription_status: serverFilters.subscription_status }),
        ...(serverFilters.search && { search: serverFilters.search })
      };
      const res = await api.getAdminServers(params);
      setServers(res.data.servers || []);
      setServerTotal(res.data.total || 0);
      setServerPages(res.data.pages || 0);
    } catch (err) {
      toast.error('Failed to load servers');
    } finally {
      setServersLoading(false);
    }
  };

  const handleFilterChange = function(key, value) {
    setServerFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
    setServerPage(1);
  };

  const openReview = async function(doc) {
    try {
      const res = await api.getAdminDocument(doc.id);
      setReviewDoc(res.data);
      setReviewStatus('verified');
      setNotes('');
      setReviewOpen(true);
    } catch (err) {
      toast.error('Failed to load document');
    }
  };

  const handleReview = async function() {
    setSubmitting(true);
    try {
      await api.verifyDocument(reviewDoc.id, reviewStatus, notes);
      toast.success(reviewStatus === 'verified' ? 'Document verified!' : 'Document rejected');
      setReviewOpen(false);
      loadData();
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSelectDoc = function(docId) {
    setSelectedDocs(prev => 
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleSelectAll = function() {
    if (selectedDocs.length === pendingDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(pendingDocs.map(d => d.id));
    }
  };

  const handleBulkAction = async function(action) {
    if (selectedDocs.length === 0) return;
    setBulkSubmitting(true);
    try {
      await Promise.all(selectedDocs.map(docId => 
        api.verifyDocument(docId, action, action === 'rejected' ? 'Bulk rejected' : '')
      ));
      toast.success(`${selectedDocs.length} documents ${action}`);
      setSelectedDocs([]);
      loadData();
    } catch (err) {
      toast.error('Bulk action failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const triggerCheck = async function() {
    try {
      await api.triggerExpirationCheck();
      toast.success('Expiration check triggered');
      loadData();
    } catch (err) {
      toast.error('Failed to trigger check');
    }
  };
  
  const loadServerCredentials = async function(serverId) {
    setCredentialsLoading(true);
    setCredentialsOpen(true);
    try {
      const response = await api.getServerCredentials(serverId);
      setCredentialsData(response.data);
    } catch (err) {
      toast.error('Failed to load credentials');
      setCredentialsOpen(false);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const toggleFoundingMember = async function(serverId, currentStatus, displayName) {
    const action = currentStatus ? 'revoke' : 'grant';
    if (!window.confirm(`Are you sure you want to ${action} Founding Member status for ${displayName}?`)) {
      return;
    }
    try {
      await api.updateFoundingMemberStatus(serverId, !currentStatus);
      toast.success(`Founding Member status ${action}ed for ${displayName}`);
      loadServers(); // Refresh the list
    } catch (err) {
      toast.error(`Failed to ${action} Founding Member status`);
    }
  };

  const sendProfileReminder = async function(serverId, displayName) {
    if (!window.confirm(`Send profile completion reminder email to ${displayName}?`)) {
      return;
    }
    try {
      const response = await api.sendProfileReminder(serverId);
      const data = response.data;
      if (data.missing_items && data.missing_items.length > 0) {
        toast.success(`Reminder sent! Missing: ${data.missing_items.join(', ')}`);
      } else {
        toast.info(data.message || 'Profile is already complete');
      }
    } catch (err) {
      toast.error('Failed to send reminder');
    }
  };

  const refreshBadges = async function(serverId, displayName) {
    try {
      const response = await api.refreshServerBadges(serverId);
      const data = response.data;
      if (data.badges_updated && data.badges_updated.length > 0) {
        toast.success(`Badges updated: ${data.badges_updated.join(', ')}`);
      } else {
        toast.info('No badge updates needed');
      }
      loadServers(); // Refresh the list
    } catch (err) {
      toast.error('Failed to refresh badges');
    }
  };

  const handleBulkRefreshBadges = async function() {
    setBulkSubmitting(true);
    try {
      const response = await api.bulkRefreshBadges();
      const data = response.data;
      toast.success(`Processed ${data.message}. Newly listed: ${data.newly_listed}, Badges updated: ${data.badges_updated}`);
      loadServers(); // Refresh the list
    } catch (err) {
      toast.error('Bulk badge refresh failed');
    }
    setBulkSubmitting(false);
  };

  const handleBulkFixListed = async function() {
    setBulkSubmitting(true);
    try {
      const response = await api.bulkFixListed();
      const data = response.data;
      if (data.fixed_count > 0) {
        toast.success(`Fixed ${data.fixed_count} servers! They are now visible in search.`);
      } else {
        toast.info('No servers needed fixing');
      }
      loadServers(); // Refresh the list
    } catch (err) {
      toast.error('Bulk fix failed');
    }
    setBulkSubmitting(false);
  };

  const changeDocumentType = async function(docId, newType, serverId) {
    try {
      const response = await api.changeDocumentType(docId, newType);
      toast.success(response.data.message);
      // Refresh credentials modal
      if (serverId) {
        loadServerCredentials(serverId);
      }
    } catch (err) {
      toast.error('Failed to change document type');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Navbar />
      <main className="flex-1 py-8">
        <div className="section-container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
              <p className="text-slate-400">Manage verifications, servers, and complaints</p>
            </div>
            <Button onClick={triggerCheck} variant="outline" className="border-slate-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Check Expirations
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.pending_verifications || 0}</p>
                  <p className="text-xs text-slate-400">Pending Docs</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.documents?.verified || 0}</p>
                  <p className="text-xs text-slate-400">Verified</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-teal-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.total_servers || 0}</p>
                  <p className="text-xs text-slate-400">Servers</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-cyan-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.active_subscriptions || 0}</p>
                  <p className="text-xs text-slate-400">Active Subs</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4 flex items-center gap-3">
                <Flag className="h-5 w-5 text-red-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{complaintStats?.pending || 0}</p>
                  <p className="text-xs text-slate-400">Complaints</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={activeTab === 'verifications' ? 'default' : 'outline'}
              onClick={() => setActiveTab('verifications')}
              className={activeTab === 'verifications' ? 'btn-accent' : 'border-slate-700'}
            >
              <FileText className="h-4 w-4 mr-2" />
              Verifications
            </Button>
            <Button
              variant={activeTab === 'servers' ? 'default' : 'outline'}
              onClick={() => setActiveTab('servers')}
              className={activeTab === 'servers' ? 'btn-accent' : 'border-slate-700'}
            >
              <Building2 className="h-4 w-4 mr-2" />
              Server Directory
            </Button>
            <Button
              variant={activeTab === 'complaints' ? 'default' : 'outline'}
              onClick={() => setActiveTab('complaints')}
              className={activeTab === 'complaints' ? 'btn-accent' : 'border-slate-700'}
            >
              <Flag className="h-4 w-4 mr-2" />
              Complaints
              {complaintStats?.pending > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">{complaintStats.pending}</span>
              )}
            </Button>
          </div>

          {/* Verifications Tab */}
          {activeTab === 'verifications' && (
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Pending Verifications</CardTitle>
                {pendingDocs.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                      className="border-slate-700 text-slate-300 text-xs"
                    >
                      {selectedDocs.length === pendingDocs.length ? (
                        <><CheckSquare className="h-3 w-3 mr-1" /> Deselect All</>
                      ) : (
                        <><Square className="h-3 w-3 mr-1" /> Select All</>
                      )}
                    </Button>
                    {selectedDocs.length > 0 && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleBulkAction('verified')}
                          disabled={bulkSubmitting}
                          className="btn-accent text-xs"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" /> Approve ({selectedDocs.length})
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleBulkAction('rejected')}
                          disabled={bulkSubmitting}
                          className="text-xs"
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Reject ({selectedDocs.length})
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {pendingDocs.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No pending verifications</p>
                ) : (
                  <div className="space-y-3">
                    {pendingDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <button onClick={() => toggleSelectDoc(doc.id)} className="text-slate-400 hover:text-white">
                            {selectedDocs.includes(doc.id) ? (
                              <CheckSquare className="h-5 w-5 text-teal-400" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                          <div>
                            <p className="font-medium text-white">{doc.server_name || 'Unknown Server'}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <span className="capitalize">{doc.document_type?.replace(/_/g, ' ')}</span>
                              {doc.state_code && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                                  {doc.state_code}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openReview(doc)} className="border-slate-700">
                          <Eye className="h-4 w-4 mr-1" /> Review
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Server Directory Tab */}
          {activeTab === 'servers' && (
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Server Directory</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                    onClick={handleBulkFixListed}
                    disabled={bulkSubmitting}
                  >
                    {bulkSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
                    Fix Unlisted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    onClick={handleBulkRefreshBadges}
                    disabled={bulkSubmitting}
                  >
                    {bulkSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Refresh All Badges
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="Search by name or email..."
                      value={serverFilters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-64 bg-slate-800/60 border-slate-700 text-white h-9"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    <Select value={serverFilters.state || 'all'} onValueChange={(v) => handleFilterChange('state', v)}>
                      <SelectTrigger className="w-32 bg-slate-800/60 border-slate-700 text-white h-9">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                        <SelectItem value="all" className="text-slate-300">All States</SelectItem>
                        {US_STATES.map(s => (
                          <SelectItem key={s} value={s} className="text-slate-300">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-slate-500" />
                    <Select value={serverFilters.subscription_status || 'all'} onValueChange={(v) => handleFilterChange('subscription_status', v)}>
                      <SelectTrigger className="w-32 bg-slate-800/60 border-slate-700 text-white h-9">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all" className="text-slate-300">All Status</SelectItem>
                        <SelectItem value="active" className="text-slate-300">Subscribers</SelectItem>
                        <SelectItem value="none" className="text-slate-300">Free Users</SelectItem>
                        <SelectItem value="cancelled" className="text-slate-300">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1" />
                  <p className="text-sm text-slate-400 self-center">
                    {serverTotal} server{serverTotal !== 1 ? 's' : ''} found
                  </p>
                </div>

                {/* Server List */}
                {serversLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
                  </div>
                ) : servers.length === 0 ? (
                  <p className="text-slate-400 text-center py-12">No servers found</p>
                ) : (
                  <div className="space-y-3">
                    {servers.map(server => {
                      // Determine status tag
                      const isSubscriber = server.subscription_status === 'active';
                      const isFree = server.subscription_status === 'none' || !server.subscription_status;
                      const isCancelled = server.subscription_status === 'cancelled';
                      const isListed = server.listed === true;
                      
                      // Determine reason for being unlisted
                      const getUnlistedReason = () => {
                        if (server.is_suspended) return 'Suspended';
                        if (!server.identity_verified) return 'No ID';
                        if (!server.coverage_areas?.length) return 'No coverage';
                        return 'Pending';
                      };
                      
                      return (
                      <div 
                        key={server.id} 
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-white truncate">{server.display_name || 'Unnamed Server'}</p>
                            {server.is_founding_member && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                FM
                              </span>
                            )}
                            {/* Subscription Status Tag */}
                            {isSubscriber && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                SUB
                              </span>
                            )}
                            {isFree && !isCancelled && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-slate-500/20 text-slate-300 border border-slate-500/30">
                                FREE
                              </span>
                            )}
                            {isCancelled && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                CANCELLED
                              </span>
                            )}
                            {/* Listed Status Tag */}
                            {!isListed && (
                              <span 
                                className="px-1.5 py-0.5 text-xs font-semibold rounded bg-red-500/20 text-red-400 border border-red-500/30"
                                title={`Reason: ${getUnlistedReason()}`}
                              >
                                UNLISTED <span className="text-red-300/70 font-normal">· {getUnlistedReason()}</span>
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {server.user_email || server.email || 'No email'}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {server.coverage_states?.join(', ') || 'No coverage'}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {server.verified_docs_count || 0} verified docs
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button 
                            size="xs" 
                            variant="outline" 
                            className={server.is_founding_member 
                              ? "border-amber-500/50 text-amber-400 hover:bg-amber-500/20 text-xs px-2 py-1 h-7" 
                              : "border-slate-700 text-xs px-2 py-1 h-7"
                            }
                            onClick={() => toggleFoundingMember(server.id, server.is_founding_member, server.display_name)}
                            title={server.is_founding_member ? "Revoke Founding Member" : "Grant Founding Member"}
                          >
                            <Award className="h-3 w-3 mr-1" />
                            {server.is_founding_member ? 'Revoke FM' : 'Grant FM'}
                          </Button>
                          <Button 
                            size="xs" 
                            variant="outline" 
                            className="border-slate-700 text-xs px-2 py-1 h-7"
                            onClick={() => loadServerCredentials(server.id)}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Creds
                          </Button>
                          <Button 
                            size="xs" 
                            variant="outline" 
                            className="border-slate-700 text-xs px-2 py-1 h-7"
                            onClick={() => sendProfileReminder(server.id, server.display_name)}
                            title="Send profile completion reminder"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Remind
                          </Button>
                          <Button 
                            size="xs" 
                            variant="outline" 
                            className="border-slate-700 text-xs px-2 py-1 h-7"
                            onClick={() => refreshBadges(server.id, server.display_name)}
                            title="Refresh badges from verified documents"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Badges
                          </Button>
                          <a href={`/servers/${server.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="xs" variant="outline" className="border-slate-700 text-xs px-2 py-1 h-7">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </Button>
                          </a>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {serverPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-700/50">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={serverPage <= 1}
                      onClick={() => setServerPage(p => p - 1)}
                      className="border-slate-700"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-400">
                      Page {serverPage} of {serverPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={serverPage >= serverPages}
                      onClick={() => setServerPage(p => p + 1)}
                      className="border-slate-700"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          {/* Complaints Tab */}
          {activeTab === 'complaints' && (
            <AdminComplaintsPanel complaintStats={complaintStats} onRefresh={loadData} />
          )}
        </div>
      </main>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Review Document</DialogTitle>
          </DialogHeader>
          {reviewDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Server</p>
                  <p className="text-white font-medium">{reviewDoc.server_name}</p>
                </div>
                <div>
                  <p className="text-slate-400">Document Type</p>
                  <p className="text-white capitalize">{reviewDoc.document_type?.replace(/_/g, ' ')}</p>
                </div>
                {reviewDoc.state_code && (
                  <div>
                    <p className="text-slate-400">State</p>
                    <p className="text-white">{reviewDoc.state_code}</p>
                  </div>
                )}
                {reviewDoc.certification_number && (
                  <div>
                    <p className="text-slate-400">Certification #</p>
                    <p className="text-white">{reviewDoc.certification_number}</p>
                  </div>
                )}
                {reviewDoc.expiration_date && (
                  <div>
                    <p className="text-slate-400">Expiration</p>
                    <p className="text-white">{reviewDoc.expiration_date}</p>
                  </div>
                )}
              </div>

              {reviewDoc.file_data && (
                <div className="border border-slate-700 rounded-lg p-4">
                  {reviewDoc.file_type?.startsWith('image/') ? (
                    <img src={reviewDoc.file_data} alt="Document" className="max-h-96 mx-auto rounded" />
                  ) : reviewDoc.file_type === 'application/pdf' ? (
                    <div className="space-y-3">
                      <iframe 
                        src={reviewDoc.file_data} 
                        title="Document Preview"
                        className="w-full h-96 rounded border border-slate-600"
                      />
                      <a 
                        href={reviewDoc.file_data} 
                        download={reviewDoc.file_name || 'document.pdf'} 
                        className="text-teal-400 hover:underline flex items-center gap-2 justify-center"
                      >
                        <FileText className="h-4 w-4" />
                        Download PDF
                      </a>
                    </div>
                  ) : (
                    <a 
                      href={reviewDoc.file_data} 
                      download={reviewDoc.file_name || 'document'} 
                      className="text-teal-400 hover:underline flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Download Document
                    </a>
                  )}
                </div>
              )}

              <div className="flex items-center gap-4">
                <Button
                  variant={reviewStatus === 'verified' ? 'default' : 'outline'}
                  onClick={() => setReviewStatus('verified')}
                  className={reviewStatus === 'verified' ? 'btn-accent' : 'border-slate-700'}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant={reviewStatus === 'rejected' ? 'destructive' : 'outline'}
                  onClick={() => setReviewStatus('rejected')}
                  className={reviewStatus !== 'rejected' ? 'border-slate-700' : ''}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>

              {reviewStatus === 'rejected' && (
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="bg-slate-800 border-slate-700 text-white"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleReview} 
              disabled={submitting || (reviewStatus === 'rejected' && !notes)}
              className={reviewStatus === 'verified' ? 'btn-accent' : 'bg-red-500'}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Server Credentials Modal */}
      <Dialog open={credentialsOpen} onOpenChange={setCredentialsOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {credentialsData ? `${credentialsData.display_name || 'Server'} - Credentials` : 'Loading...'}
            </DialogTitle>
          </DialogHeader>
          
          {credentialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
            </div>
          ) : credentialsData ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="flex flex-wrap gap-3">
                <div className="px-3 py-2 rounded bg-emerald-500/10 border border-emerald-500/30">
                  <span className="text-emerald-400 font-medium">{credentialsData.verified_count} Verified</span>
                </div>
                <div className="px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30">
                  <span className="text-amber-400 font-medium">{credentialsData.pending_count} Pending</span>
                </div>
                <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30">
                  <span className="text-red-400 font-medium">{credentialsData.expired_count} Expired</span>
                </div>
              </div>
              
              {/* Identity Verification */}
              <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
                <h3 className="flex items-center gap-2 text-white font-medium mb-3">
                  <UserCheck className="h-5 w-5 text-teal-400" />
                  Identity Verification
                </h3>
                <div className="flex items-center gap-3">
                  {credentialsData.credentials.identity.status === 'verified' ? (
                    <span className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="h-4 w-4" />
                      Verified
                    </span>
                  ) : credentialsData.credentials.identity.status === 'pending' ? (
                    <span className="flex items-center gap-2 text-amber-400">
                      <Clock className="h-4 w-4" />
                      Pending Review
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-slate-400">
                      <AlertTriangle className="h-4 w-4" />
                      Not Submitted
                    </span>
                  )}
                  {credentialsData.credentials.identity.verified_at && (
                    <span className="text-xs text-slate-500">
                      Verified: {new Date(credentialsData.credentials.identity.verified_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Document Categories */}
              {['license', 'insurance', 'bond', 'certification', 'other'].map(category => {
                const docs = credentialsData.credentials[category];
                if (!docs || docs.length === 0) return null;
                
                return (
                  <div key={category} className="p-4 rounded-lg bg-slate-800/60 border border-slate-700">
                    <h3 className="flex items-center gap-2 text-white font-medium mb-3 capitalize">
                      <Award className="h-5 w-5 text-teal-400" />
                      {category === 'license' ? 'Licenses' : 
                       category === 'insurance' ? 'Insurance' :
                       category === 'bond' ? 'Bonds' :
                       category === 'certification' ? 'Certifications' : 'Other Documents'}
                      <span className="text-xs text-slate-500">({docs.length})</span>
                    </h3>
                    <div className="space-y-3">
                      {docs.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded bg-slate-900/50 border border-slate-700/50">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <select
                                value={doc.document_type || ''}
                                onChange={(e) => changeDocumentType(doc.id, e.target.value, credentialsData.server_id)}
                                className="text-sm text-white bg-slate-800 border border-slate-600 rounded px-2 py-1 cursor-pointer hover:border-teal-500"
                                title="Change document type"
                              >
                                <option value="identity_verification">Identity Verification</option>
                                <option value="private_investigator">Private Investigator</option>
                                <option value="state_certification">State Certification</option>
                                <option value="state_license">State License</option>
                                <option value="state_registration">State Registration</option>
                                <option value="county_certification">County Certification</option>
                                <option value="insurance">Insurance</option>
                                <option value="bond">Bond</option>
                              </select>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-400">
                              {doc.issuing_authority && (
                                <span>Issuer: {doc.issuing_authority}</span>
                              )}
                              {doc.expiration_date && (
                                <span>Expires: {new Date(doc.expiration_date).toLocaleDateString()}</span>
                              )}
                              {doc.uploaded_at && (
                                <span>Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded border ${
                              doc.status === 'verified' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                              doc.status === 'pending' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                              doc.status === 'expired' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                              'bg-slate-500/20 text-slate-400 border-slate-500/30'
                            }`}>
                              {doc.status || 'unknown'}
                            </span>
                            {doc.file_data && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPreviewDoc(doc)}
                                className="text-teal-400 hover:text-teal-300 p-1 h-auto"
                                title="View document"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {credentialsData.total_documents === 0 && (
                <p className="text-slate-400 text-center py-8">No credentials uploaded yet</p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={previewDoc !== null} onOpenChange={(open) => { if (!open) setPreviewDoc(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Document Preview</DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Document Type</p>
                  <p className="text-white capitalize">{(previewDoc.document_type || 'Unknown').replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <p className={previewDoc.status === 'verified' ? 'text-emerald-400' : previewDoc.status === 'pending' ? 'text-amber-400' : previewDoc.status === 'expired' ? 'text-red-400' : 'text-slate-400'}>
                    {previewDoc.status ? previewDoc.status.charAt(0).toUpperCase() + previewDoc.status.slice(1) : 'Unknown'}
                  </p>
                </div>
                {previewDoc.issuing_authority && (
                  <div>
                    <p className="text-slate-500">Issuing Authority</p>
                    <p className="text-white">{previewDoc.issuing_authority}</p>
                  </div>
                )}
                {previewDoc.expiration_date && (
                  <div>
                    <p className="text-slate-500">Expiration Date</p>
                    <p className="text-white">{new Date(previewDoc.expiration_date).toLocaleDateString()}</p>
                  </div>
                )}
                {previewDoc.uploaded_at && (
                  <div>
                    <p className="text-slate-500">Uploaded</p>
                    <p className="text-white">{new Date(previewDoc.uploaded_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              
              {/* Document Preview */}
              <div className="mt-4">
                <p className="text-slate-500 text-sm mb-2">Document</p>
                <div className="bg-slate-800 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  {previewDoc.file_data ? (
                    previewDoc.file_type?.startsWith('image/') ? (
                      <img 
                        src={previewDoc.file_data} 
                        alt="Document" 
                        className="max-w-full max-h-[400px] object-contain rounded" 
                      />
                    ) : previewDoc.file_type === 'application/pdf' ? (
                      <div className="text-center w-full">
                        <iframe
                          src={previewDoc.file_data}
                          className="w-full h-[400px] rounded border border-slate-700"
                          title="PDF Document"
                        />
                        <a 
                          href={previewDoc.file_data} 
                          download={previewDoc.file_name || 'document.pdf'} 
                          className="text-teal-400 text-sm hover:underline mt-3 inline-block"
                        >
                          Download PDF
                        </a>
                      </div>
                    ) : (
                      <div className="text-center">
                        <FileText className="h-16 w-16 mx-auto text-slate-500 mb-2" />
                        <p className="text-slate-400">{previewDoc.file_name || 'Document'}</p>
                        <a 
                          href={previewDoc.file_data} 
                          download={previewDoc.file_name || 'document'} 
                          className="text-teal-400 text-sm hover:underline mt-2 inline-block"
                        >
                          Download File
                        </a>
                      </div>
                    )
                  ) : (
                    <p className="text-slate-500">No preview available</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPanel;
